<template>
  <header
    class="header h-[60px] bg-[#004466] fixed top-0 left-0 w-full flex items-center z-50"
  >
    <div
      class="header-container max-w-[1352px] mx-auto flex justify-between items-center w-full px-3 lg:px-0"
    >
      <div class="flex items-center gap-3">
        <button
          class="md:hidden bg-transparent border-none text-white cursor-pointer"
          @click="emit('toggle-sidebar')"
        >
          <i class="fa-solid fa-bars text-xl"></i>
        </button>
        <router-link :to="'/' + role + '-dashboard'">
          <img class="header-logo" src="@/assets/img/logo-white.png" alt="logo" />
        </router-link>
      </div>

      <div class="flex items-center gap-2 sm:gap-3">
        <div
          class="border-0 border-r-[1px] border-solid border-white pr-2 hidden lg:flex items-center"
        >
          <router-link
            v-if="role === 'teacher'"
            class="text-white font-bold"
            to="/teacher-profile"
            >{{ firstName }} {{ lastName }}
            <i class="fa-solid fa-angle-right text-xs"></i>
          </router-link>
          <a
            v-else-if="role === 'admin' || role === 'finance'"
            class="text-white font-bold no-underline hover:underline cursor-pointer"
            @click="showSalaryAuth = true"
            >{{ firstName }} {{ lastName }}
            <i class="fa-solid fa-angle-right text-xs"></i>
          </a>
          <span
            v-else
            class="text-white font-bold"
            >{{ firstName }} {{ lastName }}
          </span>
        </div>
        <div
          class="border-0 border-r-[1px] border-solid border-white pr-2 sm:pr-3 w-[72px] sm:w-[100px] hidden lg:block"
        >
          <el-select
            v-model="selectedLanguage"
            placeholder="Select Language"
            @change="changeLanguage"
          >
            <el-option
              v-for="lang in langOptions"
              :key="lang.value"
              :label="lang.label"
              :value="lang.value"
            />
          </el-select>
        </div>
        <div>
          <div
            v-if="role === 'hunter' || role === 'finance' || isStatisticsRoute"
            class="border-0 border-r-[1px] border-solid border-grayScale300 pr-2 sm:pr-3 w-[120px] sm:w-[180px]"
          >
            <el-select
              v-model="$store.state.branches.active_branch"
              :clearable="isStatisticsRoute"
              :placeholder="isStatisticsRoute ? 'Barcha filiallar' : ''"
              @change="branchSelected"
              @clear="branchSelected(null)"
            >
              <el-option
                v-if="isStatisticsRoute"
                :value="null"
                label="Barcha filiallar"
              />
              <el-option
                v-for="branch in $store.state.branches.branch_list"
                :key="branch.id"
                :label="branch.title"
                :value="branch.id"
              />
            </el-select>
          </div>
          <div
            class="border-0 border-r-[1px] border-solid border-grayScale300 pr-2 sm:pr-3 w-[120px] sm:w-[180px]"
            v-else-if="(role === 'admin' || role === 'kurator') && adminBranches?.length > 1"
          >
            <el-select
              v-model="$store.state.branches.active_branch"
              class="w-full"
              @change="branchSelected"
            >
              <el-option
                v-for="branch in adminBranches"
                :key="branch.id"
                :label="branch.title"
                :value="branch.id"
              />
            </el-select>
          </div>
        </div>

        <button
          v-if="changePasswordMode"
          @click="showChangePassword = true"
          class="settings-btn cursor-pointer hidden lg:flex justify-center items-center w-[30px] h-[30px] bg-transparent border-none"
          :title="changePasswordMode === 'pin' ? 'Change Salary PIN' : 'Change Password'"
        >
          <i class="fa-solid fa-gear text-lg text-white"></i>
        </button>

        <ChangePassword
          v-if="changePasswordMode"
          v-model="showChangePassword"
          :mode="changePasswordMode"
          @success="handlePinChanged"
        />

        <div class="header-right__logout hidden lg:block">
          <button
            class="cursor-pointer flex justify-center items-center w-[30px] h-[30px]"
            @click="logout"
          >
            <i
              class="fa-solid fa-arrow-right-from-bracket text-lg text-white"
            ></i>
          </button>
        </div>

        <!-- Mobile overflow menu (kebab): language, salary, logout -->
        <el-dropdown class="lg:hidden" trigger="click" @command="onMenuCommand">
          <button
            class="cursor-pointer flex items-center justify-center w-[30px] h-[30px] bg-transparent border-none"
          >
            <i class="fa-solid fa-ellipsis-vertical text-xl text-white"></i>
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="lang in langOptions"
                :key="lang.value"
                :command="'lang:' + lang.value"
              >
                {{ lang.label }}
                <i
                  v-if="selectedLanguage === lang.value"
                  class="fa-solid fa-check ml-2 text-success"
                ></i>
              </el-dropdown-item>
              <el-dropdown-item v-if="role === 'teacher'" divided command="profile">
                Profil
              </el-dropdown-item>
              <el-dropdown-item
                v-if="role === 'admin' || role === 'finance'"
                divided
                command="salary"
              >
                Maoshim
              </el-dropdown-item>
              <el-dropdown-item
                v-if="changePasswordMode"
                command="change-password"
              >
                {{ changePasswordMode === "pin" ? "Change Salary PIN" : "Change Password" }}
              </el-dropdown-item>
              <el-dropdown-item divided command="logout">Chiqish</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <!-- Salary PIN dialog -->
    <el-dialog
      v-model="showSalaryAuth"
      :title="hasSalaryPin ? 'Enter PIN' : 'Set Salary PIN'"
      width="360px"
      :close-on-click-modal="false"
      @close="resetPinForm"
    >
      <!-- Set new PIN -->
      <template v-if="!hasSalaryPin">
        <p class="text-gray-500 text-sm mb-3">Create a 4-digit PIN to access your salary</p>
        <el-input
          v-model="pinNew"
          type="password"
          placeholder="Enter 4-digit PIN"
          maxlength="4"
          show-password
          class="mb-3"
          @keyup.enter="$refs.pinConfirmInput?.focus()"
        />
        <el-input
          ref="pinConfirmInput"
          v-model="pinConfirm"
          type="password"
          placeholder="Confirm PIN"
          maxlength="4"
          show-password
          @keyup.enter="setSalaryPin"
        />
      </template>
      <!-- Enter existing PIN -->
      <template v-else>
        <el-input
          v-model="pinInput"
          type="password"
          placeholder="Enter 4-digit PIN"
          maxlength="4"
          show-password
          @keyup.enter="checkSalaryPin"
        />
      </template>
      <p v-if="pinError" class="text-red-500 text-sm mt-2">{{ pinError }}</p>
      <template #footer>
        <el-button @click="showSalaryAuth = false">Cancel</el-button>
        <el-button
          v-if="hasSalaryPin"
          type="primary"
          :loading="pinLoading"
          @click="checkSalaryPin"
        >Confirm</el-button>
        <el-button
          v-else
          type="primary"
          :loading="pinLoading"
          @click="setSalaryPin"
        >Set PIN</el-button>
      </template>
    </el-dialog>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";

const emit = defineEmits(["toggle-sidebar"]);
import { useStore } from "vuex";
import { decryptText } from "@/utils/cryptFunc";
import { storageGetJSON } from "@/services/storage.service";
import api from "@/apiConfig/api";
import ChangePassword from "@/modules/ChangePassword/ChangePassword.vue";
import {
  getSalaryPinStatus,
  setSalaryPin as apiSetPin,
  verifySalaryPin as apiVerifyPin,
} from "@/modules/Finance/pages/AdminSalary/api/admin-salary.api";

const firstName = ref(localStorage.getItem("firstname"));
const lastName = ref(localStorage.getItem("lastname"));
const role = ref(decryptText(localStorage.getItem("role")));
const selectedLanguage = ref(localStorage.getItem("lang") || "uz");
const langOptions = ref([
  { value: "en", label: "🇺🇸 En" },
  { value: "ru", label: "🇷🇺 Ru" },
  { value: "uz", label: "🇺🇿 Uz" },
]);
const adminBranches = ref(storageGetJSON("admin_branches", []));

const router = useRouter();
const route = useRoute();

// On /statistics/* pages the head-level user should always see every branch,
// not the access-filtered admin/kurator list. The dashboard endpoint is
// head-only so a wider picker doesn't expand permissions.
const isStatisticsRoute = computed(() =>
  (route.path || "").startsWith("/statistics"),
);
const store = useStore();

const showChangePassword = ref(false);
// admin = salary PIN, teacher = login password, everyone else: no gear.
const changePasswordMode = computed(() => {
  if (role.value === "admin") return "pin";
  if (role.value === "teacher") return "password";
  return null;
});

// Salary PIN
const showSalaryAuth = ref(false);
const pinInput = ref("");
const pinNew = ref("");
const pinConfirm = ref("");
const pinError = ref("");
const pinLoading = ref(false);
const hasSalaryPin = ref(false);

const resetPinForm = () => {
  pinInput.value = "";
  pinNew.value = "";
  pinConfirm.value = "";
  pinError.value = "";
};

const fetchPinStatus = async () => {
  try {
    const res = await getSalaryPinStatus();
    hasSalaryPin.value = res.data.has_pin;
  } catch {
    hasSalaryPin.value = false;
  }
};

const setSalaryPin = async () => {
  pinError.value = "";
  if (!/^\d{4}$/.test(pinNew.value)) {
    pinError.value = "PIN must be exactly 4 digits";
    return;
  }
  if (pinNew.value !== pinConfirm.value) {
    pinError.value = "PINs do not match";
    return;
  }
  pinLoading.value = true;
  try {
    await apiSetPin(pinNew.value, pinConfirm.value);
    hasSalaryPin.value = true;
    showSalaryAuth.value = false;
    resetPinForm();
    sessionStorage.setItem("salary_pin_verified", "1");
    router.push("/my-salary");
  } catch (err) {
    pinError.value = err.response?.data?.detail || "Failed to set PIN";
  } finally {
    pinLoading.value = false;
  }
};

const checkSalaryPin = async () => {
  pinError.value = "";
  pinLoading.value = true;
  try {
    await apiVerifyPin(pinInput.value);
    showSalaryAuth.value = false;
    resetPinForm();
    sessionStorage.setItem("salary_pin_verified", "1");
    router.push("/my-salary");
  } catch (err) {
    pinError.value = err.response?.data?.detail || "Wrong PIN";
  } finally {
    pinLoading.value = false;
  }
};

const logout = async () => {
  sessionStorage.removeItem("salary_pin_verified");
  await store.dispatch("auths/logoutAction");
  router.push("/");
};

const handlePinChanged = () => {
  hasSalaryPin.value = true;
};

const changeLanguage = (lang) => {
  localStorage.setItem("lang", lang);
  selectedLanguage.value = lang;
  window.location.reload();
};

// Mobile kebab menu — routes each item to the action its desktop control runs.
const onMenuCommand = (cmd) => {
  if (typeof cmd === "string" && cmd.startsWith("lang:")) {
    changeLanguage(cmd.slice(5));
  } else if (cmd === "profile") {
    router.push("/teacher-profile");
  } else if (cmd === "salary") {
    showSalaryAuth.value = true;
  } else if (cmd === "change-password") {
    showChangePassword.value = true;
  } else if (cmd === "logout") {
    logout();
  }
};

const branchSelected = (val) => {
  // "Barcha filiallar" (all branches, statistics-only) sends null. Persisting it
  // writes the string "null" to localStorage, which reads back as NaN on the
  // admin dashboard (+"null") and corrupts the branch picker. Clear instead.
  if (val === null || val === undefined || val === "") {
    localStorage.removeItem("branchIdInHeader");
    localStorage.removeItem("branch_id");
    return;
  }
  localStorage.setItem("branchIdInHeader", val);
  localStorage.setItem("branch_id", val);
  if (role.value === "admin") {
    saveExternalId(val);
  }
};

// Restore a valid branch when leaving /statistics. The header is persistent, so
// onMounted does not re-run on navigation — without this, the all-branches (null)
// selection from statistics would leave the admin dashboard with no branch.
watch(isStatisticsRoute, (isStats) => {
  if (isStats) return;
  if (role.value !== "admin" && role.value !== "kurator") return;
  const current = store.state.branches.active_branch;
  const isValid =
    Number.isFinite(current) &&
    adminBranches.value.some((b) => b.id === current);
  if (isValid) return;
  const fallback = adminBranches.value[0]?.id;
  if (fallback !== null && fallback !== undefined) {
    store.commit("setActiveBranchId", fallback);
    localStorage.setItem("branch_id", fallback);
    localStorage.setItem("branchIdInHeader", fallback);
  }
});

const saveExternalId = () => {
  const findBranch = adminBranches.value.find(
    (b) => b.id == store.state.branches.active_branch
  );
  if (findBranch) {
    localStorage.setItem("branch_external_id", findBranch.external_id);
    store.commit("setActiveBranchExternalId", findBranch.external_id);
  }
};

onMounted(() => {
  fetchPinStatus();

  if (role.value === "hunter" || role.value === "finance") {
    store.dispatch("getBranches");
    const branchId = parseInt(localStorage.getItem("branchIdInHeader"), 10);
    if (Number.isFinite(branchId)) {
      store.commit("setActiveBranchId", branchId);
    }
  }

  if (
    ((role.value === "admin" || role.value === "kurator") && !adminBranches.value.length) ||
    role.value === "teacher"
  ) {
    store.dispatch("getBranches");
    api.get("/me").then((res) => {
      adminBranches.value = res.data?.branches;
      localStorage.setItem(
        "admin_branches",
        JSON.stringify(res.data?.branches) || []
      );
      store.commit("setActiveBranchId", res.data?.branches[0]?.id);
      store.dispatch("setUserInfo", res.data);
    });
    saveExternalId();
  }

  if ((role.value === "admin" || role.value === "kurator") && adminBranches.value.length) {
    store.dispatch("getBranches");
    const parsed = parseInt(localStorage.getItem("branch_id"), 10);
    const branch_id = Number.isFinite(parsed)
      ? parsed
      : adminBranches.value[0]?.id;

    store.commit("setActiveBranchId", branch_id);

    // Refresh branches from API in background to pick up changes
    api.get("/me").then((res) => {
      const freshBranches = res.data?.branches || [];
      adminBranches.value = freshBranches;
      localStorage.setItem("admin_branches", JSON.stringify(freshBranches));
    });
  }

  saveExternalId();
});
</script>

<style lang="scss">
.settings-btn i {
  transition: transform 0.3s ease;
}

.settings-btn:hover i {
  transform: rotate(90deg);
}

.header {
  .el-select {
    border-bottom: 1px solid white;
    &:hover {
      box-shadow: none !important;
    }
    &__wrapper {
      background-color: transparent;
      border: none;
      box-shadow: none;
      &.is-focused {
        box-shadow: none !important;
      }

      &:hover {
        box-shadow: none !important;
      }
    }
    &__placeholder {
      color: white;
      font-weight: bold;
    }
    &__caret {
      color: white !important;
    }
  }
  .el-input__wrapper {
    border: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }
  .el-input__inner {
    color: white !important;
  }
}
</style>
